// ProfileGate — wraps any route that requires a completed customer
// profile (name + email). Composes with the page's own auth guard:
//
//   * When there's no session, the gate does nothing — the child
//     page is responsible for redirecting to /sign-in (it already
//     does so, and preserves its own returnTo).
//   * When there IS a session, the gate calls get_customer_profile.
//     If the backend reports the profile is incomplete, we redirect
//     to /account/details?returnTo=<current path+search>. Otherwise
//     the child renders untouched.
//   * While the check is in-flight we render nothing to avoid a
//     flash of the protected content before the redirect.
//
// The gate is intentionally cheap-and-safe: any RPC failure passes
// the customer through to the child (the page still guards itself
// via its own RPCs; we prefer availability over false-positive
// redirects on transient errors).

import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../lib/useSession';
import {
  rpcGetCustomerProfile,
  readProfileIsComplete,
} from '../lib/customerProfile';

export function ProfileGate({ children }) {
  const location = useLocation();
  const { session, loading: sessionLoading, configured } = useSession();
  const [status, setStatus] = useState('idle'); // 'idle' | 'checking' | 'complete' | 'incomplete' | 'unknown'
  const inflight = useRef(false);

  useEffect(() => {
    if (!configured) { setStatus('unknown'); return; }
    if (sessionLoading) return;
    if (!session) { setStatus('idle'); return; }
    if (inflight.current) return;
    inflight.current = true;
    setStatus('checking');
    rpcGetCustomerProfile().then((res) => {
      inflight.current = false;
      if (!res.ok) { setStatus('unknown'); return; }
      const complete = readProfileIsComplete(res.data);
      if (complete === true) setStatus('complete');
      else if (complete === false) setStatus('incomplete');
      else setStatus('unknown'); // fail open
    });
  }, [configured, sessionLoading, session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // No session: pass through so the child handles auth redirect.
  if (!sessionLoading && !session) return children;

  // Still checking — render nothing so we don't briefly show the
  // protected page and then yank the customer to /account/details.
  if (status === 'idle' || status === 'checking') {
    return <div data-testid="profile-gate-checking" style={{ minHeight: 1 }} />;
  }

  if (status === 'incomplete') {
    const currentPath = `${location.pathname}${location.search || ''}`;
    const to = `/account/details?returnTo=${encodeURIComponent(currentPath)}`;
    return <Navigate to={to} replace />;
  }

  // 'complete' or 'unknown' (fail open) — render the child.
  return children;
}

export default ProfileGate;
